import React, { useEffect, useRef, useState } from "react";
import { Button, Form, Input, Col, Typography, Card, message, Spin } from "antd";
import { LockOutlined, UserOutlined } from "@ant-design/icons";
import { InputOTP } from "antd-input-otp";
import { motion, AnimatePresence, Variants } from "framer-motion";
import logo from "../assets/logo.jpg";
import "../style/login.css";
import { Navigate, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { loginRequest, verifyOtpRequest } from "../redux/actions/authActions";
import { MENU_CONFIG } from "../utils/menu/menuConfig";

const { Title, Text } = Typography;

/**
 * Where to send an already-authenticated user.
 *
 * This used to be a hardcoded "/dashboard", which turned every "kick the user
 * back to /" into an infinite redirect loop: BookingLayout denies the route and
 * navigates to "/", Login immediately navigates back to /dashboard, and each
 * bounce remounted BookingLayout and refired getbrancheslist and
 * /chat/conversations. Landing on a menu the user actually has means the
 * destination cannot reject them straight back here.
 *
 * Returns null when nothing is allowed yet (auth still loading, or an account
 * with no menus) - the caller must then render the form instead of navigating.
 */
const firstAllowedPath = (
  menus?: number[],
  submenus?: number[]
): string | null => {
  const allowed = [...(menus ?? []), ...(submenus ?? [])];
  if (!allowed.length) return null;

  const match = MENU_CONFIG.find((m) => !m.parentId && allowed.includes(m.id));
  if (!match) return null;

  // /booking's own index redirects to add; live is the tab the app treats as
  // the booking landing page (see TAB_ALIASES in utils/menu/menutabs.ts).
  return match.path === "/booking" ? "/booking/live" : match.path;
};

const THEME = {
  primary: "#884299",
  primaryLight: "#a855c1",
};

const Login: React.FC = () => {
  const [form] = Form.useForm();
  const [otpValue, setOtpValue] = useState<string[]>([]);
  const [messageApi, contextHolder] = message.useMessage();
  const messageApiRef = useRef(messageApi);

  const hasNavigated = useRef(false);
  const otpMessageShown = useRef(false);

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { error, user, token, loading, menus, submenus } = useSelector(
    (state: any) => state.auth
  );

  const landingPath = firstAllowedPath(menus, submenus);

  const prevToken = useRef(token);

  const step: "login" | "otp" | "success" = token ? "success" : user ? "otp" : "login";

  const wasAuthenticatedOnMount = useRef(!!token);

  useEffect(() => {
    messageApiRef.current = messageApi;
  }, [messageApi]);

  const handleLoginSubmit = (values: any) => {
    hasNavigated.current = false;
    otpMessageShown.current = false;
    dispatch(loginRequest(values.username, values.password));
  };

  const handleOtpSubmit = () => {
    const otp = otpValue.join("");

    if (otp.length !== 6) {
      messageApi.warning("Please enter a valid 6-digit OTP.");
      return;
    }

    if (!user?.username) return;

    dispatch(verifyOtpRequest(user.username, otp));
  };

  useEffect(() => {
    const wasLoggedIn = !!prevToken.current;
    prevToken.current = token;

    if (wasLoggedIn && !token) {
      otpMessageShown.current = true;
      return;
    }

    if (user && !otpMessageShown.current) {
      otpMessageShown.current = true;
      messageApiRef.current.success(
        "OTP sent successfully! Please check your registered contact."
      );
      setOtpValue([]);
    }
  }, [user, token]);

  useEffect(() => {
    if (token && !hasNavigated.current && landingPath) {
      hasNavigated.current = true;
      messageApiRef.current.success("OTP Verified! Logged in successfully.");
      const timer = setTimeout(() => navigate(landingPath, { replace: true }), 1000);
      return () => clearTimeout(timer);
    }
  }, [token, navigate, landingPath]);

  useEffect(() => {
    if (error) {
      messageApiRef.current.error(error);
    }
  }, [error]);

  // Only bounce an already-signed-in user onward if there is somewhere they are
  // actually allowed to go. Without that check this redirect is one half of a
  // loop with BookingLayout's permission check.
  if (wasAuthenticatedOnMount.current && landingPath) {
    return <Navigate to={landingPath} replace />;
  }

  const containerVariants: Variants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.1 } },
  };

  const itemVariants: Variants = {
    hidden: { y: 25, opacity: 0 },
    visible: { y: 0, opacity: 1, transition: { duration: 0.5 } },
  };

  const cardVariants: Variants = {
    hidden: { scale: 0.95, opacity: 0, y: 50 },
    visible: {
      scale: 1,
      opacity: 1,
      y: 0,
      transition: { duration: 0.6 },
    },
  };

  return (
    <div className="login-page">
      {contextHolder}
      <Col xs={24} sm={18} md={12} lg={8}>
        <motion.div variants={containerVariants} initial="hidden" animate="visible">
          <motion.div variants={cardVariants} whileHover="hover">
            <Card
              variant="borderless"
              style={{
                width: 500,
                borderRadius: 26,
                padding: "20px",
                background: "#ffffff",
                boxShadow: "0 20px 60px rgba(0,0,0,0.08)",
              }}
            >
              <motion.div
                variants={itemVariants}
                style={{ textAlign: "center", marginBottom: 36 }}
              >
                <img
                  src={logo}
                  alt="Logo"
                  style={{
                    width: 80,
                    height: 80,
                    objectFit: "cover",
                    borderRadius: 12,
                  }}
                />
                <Title
                  level={2}
                  style={{
                    color: "#000000b4",
                    margin: "10px 0 6px",
                    fontWeight: 700,
                  }}
                >
                  Truck Taxi
                </Title>
                <Text style={{ color: "#666" }}>Booking Login Portal</Text>
              </motion.div>

              <AnimatePresence mode="wait">
                {step === "login" ? (
                  <motion.div
                    key="login"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 20 }}
                  >
                    <Form
                      form={form}
                      layout="vertical"
                      onFinish={handleLoginSubmit}
                    >
                      <motion.div variants={itemVariants}>
                        <Form.Item
                          name="username"
                          rules={[{ required: true, message: "Enter username" }]}
                        >
                          <Input
                            size="large"
                            prefix={
                              <UserOutlined style={{ color: THEME.primaryLight }} />
                            }
                            placeholder="Username"
                          />
                        </Form.Item>
                      </motion.div>

                      <motion.div variants={itemVariants}>
                        <Form.Item
                          name="password"
                          rules={[{ required: true, message: "Enter password" }]}
                        >
                          <Input.Password
                            size="large"
                            prefix={
                              <LockOutlined style={{ color: THEME.primaryLight }} />
                            }
                            placeholder="Password"
                          />
                        </Form.Item>
                      </motion.div>

                      <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                        <Button
                          type="primary"
                          htmlType="submit"
                          block
                          size="large"
                          loading={loading}
                          className="login-btn"
                        >
                          Sign In
                        </Button>
                      </motion.div>
                    </Form>
                  </motion.div>
                ) : step === "otp" ? (
                  <motion.div
                    key="otp"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                  >
                    <Title level={4} style={{ color: "#111", textAlign: "center" }}>
                      OTP Verification
                    </Title>
                    <Text
                      style={{
                        color: "#777",
                        display: "block",
                        textAlign: "center",
                        marginBottom: 16,
                      }}
                    >
                      Enter the 6-digit code sent to you
                    </Text>

                    <div className="otp-wrapper">
                      <InputOTP
                        length={6}
                        value={otpValue}
                        onChange={setOtpValue}
                        autoFocus
                        inputClassName="otp-input"
                      />
                    </div>

                    <Button
                      type="primary"
                      block
                      size="large"
                      loading={loading}
                      className="login-btn"
                      onClick={handleOtpSubmit}
                    >
                      Verify OTP
                    </Button>
                  </motion.div>
                ) : (
                  <motion.div
                    key="success"
                    initial={{ opacity: 0, scale: 0.96 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0 }}
                    style={{ textAlign: "center", padding: "24px 0" }}
                  >
                    <Spin size="large" />
                    <Title
                      level={4}
                      style={{ color: "#111", marginTop: 24, marginBottom: 4 }}
                    >
                      Logged in successfully
                    </Title>
                    <Text style={{ color: "#777" }}>Redirecting to your dashboard…</Text>
                  </motion.div>
                )}
              </AnimatePresence>
            </Card>
          </motion.div>
        </motion.div>
      </Col>

    </div>
  );
};

export default Login;